import React, { useMemo, useRef, useState } from 'react';
import { useLocation, Link } from 'react-router-dom';
import html2canvas from 'html2canvas';
import { PDFDocument, rgb } from 'pdf-lib';
import fontkit from '@pdf-lib/fontkit';
import GlassButton from './GlassButton';
import './fonts.css';

function Receipt() {
  const location = useLocation();
  const receiptRef = useRef(null);
  const [exporting, setExporting] = useState(false);

  const payload = location.state || {};
  const rows = useMemo(() => Array.isArray(payload.rows) ? payload.rows : [], [payload.rows]);

  const toPersianNumberString = (value) => {
    const n = parseInt(value) || 0;
    return n.toLocaleString('fa-IR');
  };

  // Add print-specific styles to hide everything except the receipt
  React.useEffect(() => {
    const style = document.createElement('style');
    style.textContent = `
      @media print {
        @page {
          margin: 0;
          size: 74mm 105mm;
        }
        
        html {
          zoom: 1 !important;
        }
        
        body {
          margin: 0 !important;
          padding: 0 !important;
          background: white !important;
          -webkit-print-color-adjust: exact !important;
          color-adjust: exact !important;
        }
        
        /* Hide all browser headers/footers */
        header, footer, nav, .header, .footer, 
        .title, .url, .date, .page-number,
        [data-testid="header"], [data-testid="footer"] {
          display: none !important;
        }
        
        /* Hide everything except receipt */
        body * {
          visibility: hidden !important;
        }
        
        .receipt-print-only,
        .receipt-print-only * {
          visibility: visible !important;
        }
        
        .receipt-print-only {
          position: relative !important;
          left: auto !important;
          top: auto !important;
          width: 74mm !important;
          height: 105mm !important;
          background: white !important;
          margin: 0 auto !important;
          border-radius: 0 !important;
          border: none !important;
          transform: scale(1) !important;
          zoom: 1 !important;
        }
        
        /* Force print preview to show actual size */
        * {
          -webkit-print-color-adjust: exact !important;
          color-adjust: exact !important;
        }
      }
    `;
    document.head.appendChild(style);
    return () => {
      document.head.removeChild(style);
    };
  }, []);

  const dataUrlToUint8Array = (dataUrl) => {
    const parts = String(dataUrl || '').split(',');
    const base64 = parts.length > 1 ? parts[1] : '';
    const binary = atob(base64);
    const bytes = new Uint8Array(binary.length);
    for (let i = 0; i < binary.length; i += 1) {
      bytes[i] = binary.charCodeAt(i);
    }
    return bytes;
  };

  const downloadBlob = (bytes, filename) => {
    const blob = new Blob([bytes], { type: 'application/pdf' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  };

  const hasPdfHeader = (arrayBuffer) => {
    try {
      const bytes = new Uint8Array(arrayBuffer);
      if (bytes.length < 5) return false;
      return (
        bytes[0] === 0x25 && // %
        bytes[1] === 0x50 && // P
        bytes[2] === 0x44 && // D
        bytes[3] === 0x46 && // F
        bytes[4] === 0x2d    // -
      );
    } catch {
      return false;
    }
  };

  const tryDownloadFromTemplate = async () => {
    const templateCandidates = ['/ذکر.pdf', '/zekr.pdf', '/dhikr.pdf', '/template.pdf'];
    let templateBytes = null;
    for (const path of templateCandidates) {
      try {
        const res = await fetch(path);
        if (!res.ok) continue;

        const contentType = (res.headers && res.headers.get) ? (res.headers.get('content-type') || '') : '';
        const bytes = await res.arrayBuffer();

        // In CRA dev server, missing files may return index.html with 200.
        // Guard against trying to parse HTML as PDF.
        const looksLikePdf = contentType.toLowerCase().includes('pdf') || hasPdfHeader(bytes);
        if (!looksLikePdf) continue;

        templateBytes = bytes;
        break;
      } catch (e) {
        // ignore
      }
    }

    if (!templateBytes) return false;

    let pdfDoc;
    try {
      pdfDoc = await PDFDocument.load(templateBytes);
    } catch (e) {
      return false;
    }
    pdfDoc.registerFontkit(fontkit);

    let font = null;
    try {
      const fontRes = await fetch('/fonts/Far_Naskh.ttf');
      if (fontRes.ok) {
        const fontBytes = await fontRes.arrayBuffer();
        font = await pdfDoc.embedFont(fontBytes, { subset: true });
      }
    } catch (e) {
      // ignore
    }

    const page = pdfDoc.getPages()[0];
    const { width, height } = page.getSize();

    const countCenterX = width * 0.78;
    const rowYs = [0.57, 0.44, 0.31, 0.18].map(r => height * r);
    const fontSize = 18;
    const textColor = rgb(0, 0, 0);

    rows.slice(0, 4).forEach((r, idx) => {
      const y = rowYs[idx];
      if (!y) return;
      const text = toPersianNumberString(r?.registered);
      const textWidth = font ? font.widthOfTextAtSize(text, fontSize) : (text.length * fontSize * 0.5);
      const x = countCenterX - (textWidth / 2);
      page.drawText(text, {
        x,
        y,
        size: fontSize,
        font: font || undefined,
        color: textColor
      });
    });

    const pdfBytes = await pdfDoc.save();
    downloadBlob(pdfBytes, 'receipt-template.pdf');
    return true;
  };

  const downloadPdfFromPreview = async () => {
    if (!receiptRef.current) return;

    setExporting(true);
    try {
      await new Promise((resolve) => requestAnimationFrame(resolve));
      await new Promise((resolve) => requestAnimationFrame(resolve));

      const canvas = await html2canvas(receiptRef.current, {
        backgroundColor: '#ffffff',
        // High scale plus JPEG quality 1.0 to minimize viewer artifacts.
        scale: 6,
        useCORS: true
      });

      // Get a high-quality JPEG blob and embed it directly.
      const jpegBlob = await new Promise((resolve) =>
        canvas.toBlob(resolve, 'image/jpeg', 1.0)
      );
      const jpegBytes = await jpegBlob.arrayBuffer();

      const pdfDoc = await PDFDocument.create();
      const page = pdfDoc.addPage([canvas.width, canvas.height]);
      const jpegImage = await pdfDoc.embedJpg(jpegBytes);
      page.drawImage(jpegImage, {
        x: 0,
        y: 0,
        width: canvas.width,
        height: canvas.height
      });

      const pdfBytes = await pdfDoc.save();
      downloadBlob(pdfBytes, 'receipt-preview.pdf');
    } finally {
      setExporting(false);
    }
  };

  const downloadPdfFromTemplate = async () => {
    const usedTemplate = await tryDownloadFromTemplate();
    if (!usedTemplate) {
      await downloadPdfFromPreview();
    }
  };

  return (
    <div style={{
      width: '100vw',
      height: '100vh',
      backgroundColor: '#000000',
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      padding: '20px',
      boxSizing: 'border-box'
    }}>
      <div style={{
        display: 'flex',
        flexDirection: 'column',
        gap: '12px',
        alignItems: 'center'
      }}>
        <div
          ref={receiptRef}
          className="receipt-print-only"
          style={{
            width: '74mm',
            height: '105mm',
            background: '#ffffff',
            color: '#111111',
            borderRadius: exporting ? 0 : '10mm',
            border: exporting ? 'none' : '1.2mm solid #111111',
            padding: '5mm',
            boxSizing: 'border-box',
            fontFamily: 'AbarMidFaNum, Arial, sans-serif',
            overflow: 'hidden'
          }}
        >
          <div style={{
            height: '22mm',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            marginBottom: '3mm'
          }}>
            <img
              src="/logo.png"
              alt="logo"
              style={{
                width: '26mm',
                height: '26mm',
                objectFit: 'contain'
              }}
            />
          </div>

          <div style={{
            display: 'flex',
            flexDirection: 'column',
            height: 'calc(100% - 25mm)',
            justifyContent: 'space-between'
          }}>
            <table style={{
              width: '100%',
              borderCollapse: 'collapse',
              tableLayout: 'fixed'
            }}>
            <thead>
              <tr>
                <th style={{
                  border: '0.5mm solid #111111',
                  padding: '2.2mm 2mm',
                  textAlign: 'center',
                  background: '#d9d9d9',
                  fontSize: '14px',
                  fontWeight: 'bold',
                  width: '50%'
                }}>
                  ذکر
                </th>
                <th style={{
                  border: '0.5mm solid #111111',
                  padding: '2.2mm 2mm',
                  textAlign: 'center',
                  background: '#d9d9d9',
                  fontSize: '14px',
                  fontWeight: 'bold',
                  width: '50%'
                }}>
                  تعداد
                </th>
              </tr>
            </thead>
            <tbody>
              {rows.slice(0, 4).map((r, idx) => {
                const imagesByIndex = ['/salawat.png', '/kon.png', '/la.png', '/asta.png'];
                const imgSrc = imagesByIndex[idx] || null;
                const isCompressedRow = idx === 1 || idx === 2;
                const rowHeight = isCompressedRow ? '8mm' : undefined;
                const isFirstOrFourthRow = idx === 0 || idx === 3;
                const cellPadding = isFirstOrFourthRow ? '1mm 1mm' : '2mm 1mm';
                return (
                  <tr key={idx}>
                    <td style={{
                      border: '0.5mm solid #111111',
                      padding: cellPadding,
                      width: '50%',
                      overflow: 'hidden',
                      height: rowHeight
                    }}>
                      <div style={{
                        width: '100%',
                        height: rowHeight,
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        overflow: 'hidden'
                      }}>
                        <img
                          src={imgSrc || '/logo.png'}
                          alt={r.name}
                          style={{
                            height: isCompressedRow ? '8mm' : '14mm',
                            width: 'auto',
                            maxWidth: '100%',
                            objectFit: 'contain',
                            display: 'block'
                          }}
                        />
                      </div>
                    </td>
                    <td style={{
                      border: '0.5mm solid #111111',
                      padding: cellPadding,
                      width: '50%',
                      height: rowHeight,
                      fontFamily: 'AbarMidFaNum, Arial, sans-serif',
                      fontSize: '16px',
                      fontWeight: 'bold'
                    }}>
                      <div style={{
                        width: '100%',
                        height: rowHeight,
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center'
                      }}>
                        {(r.registered || 0).toLocaleString('fa-IR')}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
            </table>

                      </div>
        </div>

        <div style={{ display: 'flex', gap: '10px' }}>
          <GlassButton
            onClick={downloadPdfFromPreview}
            style={{
              padding: '10px 16px',
              fontSize: '12px',
              background: 'rgba(76, 175, 80, 0.2)',
              border: '1px solid rgba(76, 175, 80, 0.5)',
              fontFamily: 'AbarMidFaNum, Arial, sans-serif'
            }}
          >
            دانلود PDF
          </GlassButton>

          <GlassButton
            onClick={() => window.print()}
            style={{
              padding: '10px 16px',
              fontSize: '12px',
              background: 'rgba(33, 150, 243, 0.2)',
              border: '1px solid rgba(33, 150, 243, 0.5)',
              fontFamily: 'AbarMidFaNum, Arial, sans-serif'
            }}
          >
            پرینت
          </GlassButton>

          <Link to="/admin" style={{ textDecoration: 'none' }}>
            <GlassButton
              style={{
                padding: '10px 16px',
                fontSize: '12px',
                background: 'rgba(255, 152, 0, 0.2)',
                border: '1px solid rgba(255, 152, 0, 0.5)',
                fontFamily: 'AbarMidFaNum, Arial, sans-serif'
              }}
            >
              بازگشت
            </GlassButton>
          </Link>
        </div>
      </div>
    </div>
  );
}

export default Receipt;
